import { useSelect } from 'context/select-context'
import classNames from 'helpers/class-names'
import { useRouter } from 'next/router'
import { FC, MouseEvent, ReactNode } from 'react'
import { Tooltip } from 'ui'
import { useListItem } from '@floating-ui/react'

type Props = {
  onClick?: () => void
  label: string
  description: string
  disabled?: boolean
  context?: 'neutral' | 'negative'
  href?: string
  IconComponent?: FC<{ className?: string }>
  tooltip?: string
}

const StackedItem: FC<Props> = ({
  onClick,
  href,
  label,
  description,
  context = 'neutral',
  IconComponent,
  disabled,
  tooltip,
}) => {
  const { activeIndex, getItemProps, handleSelect } = useSelect()
  const { ref, index } = useListItem({
    label: typeof label === 'string' ? label : null,
  })

  const router = useRouter()
  const isActive = activeIndex === index

  const itemClasses = (active: boolean): string =>
    classNames(
      'group flex w-full items-start rounded-md p-2 text-left tracking-wide',
      'text-sm dark:text-gray-200 outline-none',
      {
        'cursor-not-allowed border-gray-200 text-gray-300 active:bg-gray-200':
          disabled,
        'bg-info-lighten dark:bg-gray-600':
          !disabled && active && context === 'neutral',
        'bg-red-100 dark:bg-gray-600':
          !disabled && active && context === 'negative',
        'text-gray-500': !disabled && context === 'neutral',
        ' text-danger': !disabled && context === 'negative',
      },
    )

  const content = (active: boolean) => (
    <>
      {Boolean(IconComponent) && (
        <IconComponent
          className={classNames('mr-2 h-6 w-6', {
            'text-info': context === 'neutral',
          })}
          aria-hidden="true"
        />
      )}
      <div className="flex-1">
        <div className="font-semibold text-gray">{label}</div>
        <div className="mt-px text-xs text-gray-300">{description}</div>
      </div>
    </>
  )

  return (
    <li className="px-2 first:mt-2 last:mb-2">
      <Tooltip tip={tooltip}>
        <button
          ref={ref}
          tabIndex={isActive ? 0 : -1}
          disabled={disabled}
          className={classNames(itemClasses(isActive), 'font-body font-normal')}
          {...getItemProps({
            onClick: (event: MouseEvent) => {
              event.stopPropagation()
              if (disabled) {
                return
              }

              if (typeof onClick === 'function') {
                onClick()
              }

              if (href) {
                router.push(href)
              }
              handleSelect(index)
            },
          })}>
          {content(isActive)}
        </button>
      </Tooltip>
    </li>
  )
}

export default StackedItem
